import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Lock, Eye, EyeOff, Users, ScrollText, Swords } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const { login, register, loginWithGoogle } = useAuth();
  const [tab, setTab] = useState("signin");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (tab === "signin") {
        await login(email, password);
        toast.success("Welcome back, adventurer!");
      } else {
        await register(email, username, password);
        toast.success("Your account is ready!");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-5 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="flex flex-col items-center mb-8"
      >
        <Logo size="lg" />
        <p className="text-zinc-500 mt-4 text-sm">Your Homebrew D&D Character Portal</p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
        className="w-full max-w-md glass rounded-3xl p-6 sm:p-8"
      >
        <div className="grid grid-cols-2 gap-1 p-1 rounded-2xl bg-white/[0.03] border border-white/[0.06] mb-7">
          {[["signin", "Sign In"], ["signup", "Create Account"]].map(([k, label]) => (
            <button
              key={k}
              data-testid={`tab-${k}`}
              onClick={() => setTab(k)}
              className={`py-2.5 rounded-xl text-sm font-semibold transition ${
                tab === k ? "btn-violet" : "text-zinc-400 hover:text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs font-medium text-zinc-400 uppercase tracking-wide">
              {tab === "signin" ? "Email or Username" : "Email"}
            </label>
            <div className="relative mt-1.5">
              <Mail size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                data-testid="login-email"
                type="text"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={tab === "signin" ? "Email or username" : "you@example.com"}
                className="input-dark w-full rounded-xl pl-11 pr-3.5 py-3 text-sm"
              />
            </div>
          </div>

          {tab === "signup" && (
            <div>
              <label className="text-xs font-medium text-zinc-400 uppercase tracking-wide">Username</label>
              <div className="relative mt-1.5">
                <Users size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  data-testid="signup-username"
                  type="text"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Choose a username"
                  className="input-dark w-full rounded-xl pl-11 pr-3.5 py-3 text-sm"
                />
              </div>
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-zinc-400 uppercase tracking-wide">Password</label>
            <div className="relative mt-1.5">
              <Lock size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                data-testid="login-password"
                type={show ? "text" : "password"}
                required
                minLength={tab === "signup" ? 8 : 6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="input-dark w-full rounded-xl pl-11 pr-11 py-3 text-sm"
              />
              <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
                {show ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>
          </div>

          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="btn-violet w-full rounded-xl py-3 text-sm font-bold disabled:opacity-60"
          >
            {loading ? "Please wait…" : tab === "signin" ? "Sign In" : "Create Account"}
          </button>
        </form>

        <div className="flex items-center gap-3 my-6">
          <div className="h-px flex-1 bg-white/[0.07]" />
          <span className="text-xs text-zinc-600">or continue with</span>
          <div className="h-px flex-1 bg-white/[0.07]" />
        </div>

        <div className="flex justify-center" data-testid="google-login">
          <GoogleSignInButton
            onCredential={async (credential) => {
              try {
                await loginWithGoogle(credential);
                toast.success("Signed in with Google!");
              } catch (err) {
                toast.error(formatApiError(err.response?.data?.detail) || "Google sign-in failed");
              }
            }}
          />
        </div>

        <p className="text-center text-[11px] text-zinc-600 mt-4">
          Launched from BeyondTheTable? You're signed in automatically.
        </p>

        <div className="grid grid-cols-3 gap-3 mt-6">
          {[[Users, "Campaigns", "text-violet-400"], [ScrollText, "Cards", "text-amber-400"], [Swords, "Characters", "text-teal-400"]].map(([Icon, label, color]) => (
            <div key={label} className="glass rounded-2xl py-4 flex flex-col items-center gap-1.5">
              <Icon size={20} className={color} />
              <span className="text-[11px] text-zinc-500">{label}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}
